/// <amd-module name="Wiki/ViewCommon/Utils/ProjectNameValidator" />
import { equals, format } from "VSS/Core/Util/String";
import * as Resources from "Wiki/ViewCommon/Resources";

// copied from Tfs/Web/extensions/tfs/vss-admin-web/util/ProjectNameValidator.ts and added error messages for invalid project name.

const _invalidProjectNames = [
    "server",
    "bin",
    "App_code",
    "App_Browsers",
    "App_Data",
    "App_GlobalResources",
    "App_LocalResources",
    "App_Themes",
    "App_WebReferences",
    "web.config",
    "SignalR"
];

const _invalidCssNodeNames = [
    "prn",
    "com1",
    "com2",
    "com3",
    "com4",
    "com5",
    "com6",
    "com7",
    "com8",
    "com9",
    "com10",
    "lpt1",
    "lpt2",
    "lpt3",
    "lpt4",
    "lpt5",
    "lpt6",
    "lpt7",
    "lpt8",
    "lpt9",
    "nul",
    "con",
    "aux",
    ".",
    ".."
];

const _invalidProjectCharacters = [
    "@",
    "~",
    ";",
    "{",
    "}",
    "'",
    "+",
    "=",
    ",",
    "<",
    ">",
    "|",
    "/",
    "\\",
    "?",
    ":",
    "&",
    "$",
    "*",
    '"',
    "#",
    "[",
    "]",
    "&",
    "%"
];

const _maxProjectLength = 64;

export interface ValidationResult {
    isValid: boolean;
    invalidNameReason: string;
}

/**
 * Returns true if the given project name is valid.
 * @param projectName
 */
export function validateProjectName(projectName: string): ValidationResult {
    let index = 0;
    const validationResult: ValidationResult = {
        isValid: true,
        invalidNameReason: ""
    };

    // Account names must be 1 or more characters and cant start with
    // '.' or '_' and cant end with '.'
    // These are requirements from CssUtil.IsValidProjectName()

    projectName = projectName.trim();

    if (projectName === null || projectName.length === 0) {
        validationResult.isValid = false;
        validationResult.invalidNameReason = Resources.WikiNameEmpty;
    } else if (projectName.length > _maxProjectLength) {
        validationResult.isValid = false;
        validationResult.invalidNameReason = format(Resources.WikiNameTooLong, _maxProjectLength);
    } else if (projectName.substring(0, 1) === "_" || projectName.substring(0, 1) === ".") {
        validationResult.isValid = false;
        validationResult.invalidNameReason = Resources.WikiNameCannotStartWith;
    } else if (projectName.charAt(projectName.length - 1) === ".") {
        validationResult.isValid = false;
        validationResult.invalidNameReason = Resources.WikiNameCannotEndWith;
    } else {
        const invalidChars: string[] = [];
        // Check for invalid characters within the project name.
        for (index = 0; index < _invalidProjectCharacters.length; index++) {
            const lookupChar = _invalidProjectCharacters[index];
            if (projectName.indexOf(lookupChar) !== -1) {
                validationResult.isValid = false;
                // Append all invalid characters
                if (invalidChars.indexOf(lookupChar) === -1) {
                    invalidChars.push(lookupChar);
                }
            }
        }

        if (!validationResult.isValid) {
            validationResult.invalidNameReason = format(Resources.InvalidCharactersInWikiName, invalidChars.join("','"));
        } else {
            const invalidNames = [..._invalidCssNodeNames, ..._invalidProjectNames];

            // Check for invalid css node names.
            for (index = 0; index < invalidNames.length; index++) {
                if (equals(projectName, invalidNames[index], true)) {
                    validationResult.isValid = false;
                    validationResult.invalidNameReason = format(Resources.ReservedNameNotAllowedInWikiName, projectName);
                    break;
                }
            }
        }
    }

    return validationResult;
}
